import classNames from "classnames";
import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useLayoutEffect,
  useImperativeHandle,
} from "react";
import { usePopper } from "react-popper";
import { useMediaQuery } from "react-responsive";

import { noop } from "@helpers/common";
import useClickAway from "@hooks/useClickAway";

import { XL } from "@const/breakpoints";

import styles from "./index.module.scss";

const TextTooltip = (
  {
    text,
    className,
    tooltipClassName,
    wrapperClassName,
    wrapperClassNameWhenTooltipVisible,
    children,
    placement = "top",
    withArrow = true,
    toolTipBody,
    withNoArrow,
    offset = [0, 8],
    strategy = "fixed",
    handleClick: propHandleClick = noop,
  },
  ref,
) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const wrapperRef = useRef(null);

  const isDesktop = useMediaQuery({
    query: `(min-width: ${XL})`,
  });

  useEffect(() => {
    const scrollHandler = () => {
      setIsVisible(false);
    };

    window.addEventListener("scroll", scrollHandler);

    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  useLayoutEffect(() => {
    if (isVisible && !isDesktop) update();
  }, [isVisible]);

  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(referenceElement, popperElement, {
    placement,
    strategy,
    modifiers: [
      {
        name: "offset",
        options: {
          offset,
        },
      },
      { name: "arrow", options: { element: arrowElement } },
    ],
  });

  const handleHover = () => {
    if (!isDesktop) return;
    update();
    setIsVisible(true);
  };

  const handleClick = () => {
    if (isDesktop) return;
    update();
    propHandleClick();
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    if (!isDesktop) return;
    setIsVisible(false);
  };

  useClickAway({ current: referenceElement }, () => {
    if (isDesktop) return;
    update();
    setIsVisible(false);
  });

  useImperativeHandle(ref, () => ({
    setVisible: (v) => {
      update();
      setIsVisible(v);
    },
  }));

  return (
    <div
      ref={wrapperRef}
      className={classNames(
        wrapperClassName,
        isVisible && wrapperClassNameWhenTooltipVisible,
      )}
    >
      <div
        ref={setReferenceElement}
        onMouseEnter={handleHover}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className={className}
      >
        {children}
      </div>
      <div
        ref={setPopperElement}
        className={classNames(
          "text-break word-wrap",
          styles.tooltip,
          tooltipClassName,
        )}
        {...attributes.popper}
        style={{
          ...popperStyles.popper,
          opacity: isVisible ? 1 : 0,
          pointerEvents: "none",
          zIndex: isVisible ? "9999999999" : "-999",
          display: !isDesktop ? (isVisible ? "block" : "none") : "block",
        }}
      >
        {toolTipBody ? toolTipBody() : text}
        {!withNoArrow && (
          <div
            className={withArrow && styles.arrow}
            ref={setArrowElement}
            style={popperStyles.arrow}
          />
        )}
      </div>
    </div>
  );
};

export default forwardRef(TextTooltip);
